import React, { useEffect, useState } from "react";
import {
  Card,
  Tag,
  Progress,
  Button,
  Dropdown,
  Menu,
  Row,
  Col,
  Skeleton,
  Checkbox,
  Avatar,
  Tooltip,
} from "antd";
import {
  EyeOutlined,
  MailOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { CustomSecondaryButton } from "../../physician_page/styled";
import journeyService from "../../../../services/journey";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import {
  JourneyCardWrapper,
  CardHeader,
  HeaderContent,
  CardTitle,
  CardDescription,
  StatusTag,
  ProgressContainer,
  ProgressText,
  StyledProgress,
  DateInfo,
  CardActions,
  PreviewButton,
  MenuIcon,
  EmptyStateCard,
  EmptyStateText,
  CustomCheckBox,
} from "./Styled";
import CreateJourneyModal from "../modals/CreateJourneyModal";
import DeleteConfirmationModal from "../../physician_page/component/DeleteConfirmationModal";
import { notify } from "../../../../utils/helper";
import { capitalizeFirstLetter } from "../../../../utils/utils";
import { FiUserPlus } from "react-icons/fi";
import AssignJourneyModal from "../modals/AssignJourneyModal";
import { StyledMenu } from "./TemplateCard";

// Component-specific styled components
const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: var(--secondary-text-color);

  strong {
    color: var(--primary-text-color);
    font-weight: 600;
    margin-right: 8px;
    min-width: 80px;
  }
`;

const InfoLabel = styled.div`
  font-size: 14px;
  color: var(--secondary-text-color);
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--primary-text-color);
`;

export const ReminderButton = styled(CustomSecondaryButton)`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  padding: 8px 16px;
  border: 1px solid var(--border-color);
`;

const ClinicalJourneyCard = ({
  selectedClinicalId,
  setSelectedClinicalId,
  fetchJourneys,
  journeys,
  loading,
  setJourneyName,
  journeyData,
  setJourneyData,
  currentPage,
  hasMore,
  loadMoreLoading,
  setIsAssignJourneyModalOpen,
}) => {
  const history = useHistory();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isJourneyEdit, setIsJourneyEdit] = useState(false);
  const [selectedJourneyId, setSelectedJourneyId] = useState([]);
  const [assignJourneyModal, setAssignJourneyModal] = useState(false);

  useEffect(() => {
    fetchJourneys("clinical", 1, false);
    setJourneyName("clinical");
  }, []);

  const handleLoadMore = () => {
    fetchJourneys("clinical", currentPage + 1, true);
  };

  const handleSuccess = () => {
    fetchJourneys("clinical");
  };

  const handlePreviewClick = (journey) => {
    const id = journey?.id || journey?._id;
    if (id) {
      history.push(`/pages/journeys/details/${id}`);
    }
  };

  const handleSendReminderClick = (journey) => {
    // TODO: Implement send reminder functionality
  };

  const handleViewDetails = (journey) => {
    const id = journey?.id || journey?._id;
    if (id) {
      history.push(`/pages/journeys/details/${id}`);
    }
  };

  const handleEditJourney = (journey) => {
    setSelectedJourney(journey);
    setIsEditModalOpen(true);
    setIsJourneyEdit(true);
  };

  const handleAssignJourney = (journeyId) => {
    setJourneyData(journeyId);
    setSelectedJourneyId(journeyId?.id);
    setAssignJourneyModal(true);
  };

  const handleDeleteJourney = (journey) => {
    setSelectedJourney(journey);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedJourney) return;
    try {
      setDeleteLoading(true);
      const id = selectedJourney?.id || selectedJourney?._id;
      await journeyService.deleteJourney({ ids: [id] });
      notify("success", "Journey deleted successfully");
      setIsDeleteModalOpen(false);
      setSelectedJourney(null);
      fetchJourneys("clinical");
    } catch (error) {
      notify("error", "Failed to delete journey");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEditSuccess = () => {
    setSelectedClinicalId("");
    fetchJourneys("clinical");
  };

  const handleAssignButton = (journey) => {
    if (journey) {
      setSelectedClinicalId(journey.id);
      setJourneyData(journey);
      setIsAssignJourneyModalOpen(true);
    } else {
      setSelectedClinicalId("");
      setJourneyData([]);
    }
  };

  const handleStatusChange = async (status, journey) => {
    try {
      await journeyService.journeyStatusChange({ id: journey.id, status });
      fetchJourneys("clinical");
      notify("success", "Journey status updated successfully.");
    } catch (err) {
      notify("error", "Failed to update status.");
    }
  };

  const statusMenu = (journey) => (

    <StyledMenu onClick={({ key }) => handleStatusChange(key, journey)}>
      <Menu.Item key="draft">
        <span className="menu-item-text">Draft</span>
      </Menu.Item>
      <Menu.Item key="active">
        <span className="menu-item-text">Active</span>
      </Menu.Item>
      <Menu.Item key="completed">
        <span className="menu-item-text">Completed</span>
      </Menu.Item>
    </StyledMenu>
  );

  const menu = (journey) => (
    <StyledMenu>
      <Menu.Item
        key="view"
        onClick={() => handleViewDetails(journey)}
      >
        <div className="menu-item-content">
          <EyeOutlined className="menu-item-icon" />
          <span className="menu-item-text">View Details</span>
        </div>
      </Menu.Item>
      <Menu.Item
        key="edit"
        onClick={() => handleEditJourney(journey)}
      >
        <div className="menu-item-content">
          <EditOutlined className="menu-item-icon" />
          <span className="menu-item-text">Edit Journey</span>
        </div>
      </Menu.Item>
      {/* {journey?.assignedPatients.length === 0 && ( */}
        <Menu.Item
          key="assign"
          onClick={() => handleAssignJourney(journey)}
        >
          <div className="menu-item-content">
            <FiUserPlus className="menu-item-icon" />
            <span className="menu-item-text">Assign Journey</span>
          </div>
        </Menu.Item>
      {/* )} */}
      <Menu.Divider style={{color: "var(border-color)"}} />
      <Menu.Item
        key="delete"
        onClick={() => handleDeleteJourney(journey)}
      >
        <div className="menu-item-content">
          <DeleteOutlined className="menu-danger-icon" />
          <span className="menu-danger-text">Delete</span>
        </div>
      </Menu.Item>
    </StyledMenu>
  );

  if (loading) {
    return (
      <Row gutter={[16, 16]} style={{ width: "100%" }}>
        {[1, 2, 3].map((idx) => (
          <Col span={8} key={idx}>
            <JourneyCardWrapper>
              <CardHeader>
                <HeaderContent>
                  <Skeleton.Button
                    active
                    size="small"
                    style={{ width: 80, height: 24 }}
                  />
                </HeaderContent>
                <Skeleton.Button
                  active
                  size="small"
                  style={{ width: 24, height: 24 }}
                />
              </CardHeader>
              <Skeleton
                active
                paragraph={{ rows: 2 }}
                style={{ marginBottom: 16 }}
              />

              <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                {[1, 2, 3, 4].map((i) => (
                  <Col xs={12} sm={12} lg={6} key={i}>
                    <Skeleton active paragraph={{ rows: 2 }} />
                  </Col>
                ))}
              </Row>
              <Skeleton
                active
                paragraph={{ rows: 1 }}
                style={{ marginBottom: 16 }}
              />
              <CardActions>
                <Skeleton.Button
                  active
                  size="default"
                  style={{ flex: 1, height: 36 }}
                />
                <Skeleton.Button
                  active
                  size="default"
                  style={{ flex: 1, height: 36 }}
                />
              </CardActions>
            </JourneyCardWrapper>
          </Col>
        ))}
      </Row>
    );
  }

  if (journeys?.length === 0) {
    return (
      <Row gutter={[16, 16]} style={{ width: "100%" }}>
        <Col span={24}>
          <EmptyStateCard>
            <EmptyStateText>No clinical journeys available</EmptyStateText>
          </EmptyStateCard>
        </Col>
      </Row>
    );
  }

  return (
    <>
      {isEditModalOpen && (
        <CreateJourneyModal
          isOpen={isEditModalOpen}
          setOpen={setIsEditModalOpen}
          isEdit={isJourneyEdit}
          setIsEdit={setIsJourneyEdit}
          journeyId={selectedJourney}
          onSuccess={handleEditSuccess}
        />
      )}
      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          setOpen={setIsDeleteModalOpen}
          onConfiem={handleConfirmDelete}
          loading={deleteLoading}
          title="Delete Journey"
          message="Are you sure you want to delete this journey? This action cannot be undone."
        />
      )}
      {assignJourneyModal && (
        <AssignJourneyModal
          isOpen={assignJourneyModal}
          setOpen={setAssignJourneyModal}
          journeyId={selectedJourneyId}
          onSuccess={handleSuccess}
          selectedClinicalId={selectedClinicalId}
          setSelectedClinicalId={setSelectedClinicalId}
          journeyData={journeyData}
        />
      )}
      <Row gutter={[16, 16]}>
        {journeys?.map((journey, idx) => {
          const journeyCompleted = journey?.assignedPatients[0];
          const totalSteps = journey?.steps?.length;
          const progress = journey?.progress || 0;

          return (
            <Col
              lg={8}
              md={12}
              sm={24}
              xs={24}
              key={journey?.id || journey?._id || idx}
            >
              <JourneyCardWrapper key={journey?.id || journey?._id || idx}>
                <CardHeader>
                  <HeaderContent style={{maxWidth: "87%"}}>
                    <CardTitle>
                      {/* {journey?.assignedPatients.length === 0 && (
                        <CustomCheckBox> */}
                          {/* <Checkbox
                            checked={selectedClinicalId.includes(journey.id)}
                            onChange={(e) =>
                              handleCheckbox(e.target.checked, journey)
                            }
                          /> */}
                        {/* </CustomCheckBox>
                      )} */}
                      <span className="ellipsis">
                      {capitalizeFirstLetter(
                        journey?.name || "Untitled Journey"
                      )}
                      </span>
                    </CardTitle>
                    <CardDescription>
                      {capitalizeFirstLetter(
                        journey?.description || "No description available"
                      )}
                    </CardDescription>
                  </HeaderContent>
                  <Dropdown overlay={menu(journey)} trigger={["click"]} getPopupContainer={triggerNode => triggerNode.parentNode}>
                    <MenuIcon />
                  </Dropdown>
                </CardHeader>

                <Row gutter={[16, 12]} style={{ marginBottom: 16 }}>
                  <Col xs={24} sm={12}>
                    {" "}
                    <InfoLabel>Patient</InfoLabel>
                    {(() => {
                      const list = journey?.assignedPatients || [];

                      if (list.length === 0) return <InfoValue>N/A</InfoValue>;
                      if (list.length === 1) return <InfoValue>{list[0]?.patientId?.name}</InfoValue>;

                      return (
                        <Avatar.Group 
                          maxCount={2} 
                          size="small"
                        >
                          {list.map((item) => (
                            <Tooltip
                              title={capitalizeFirstLetter(item?.patientId?.name)}
                              key={item?.patientId?.id}
                            >
                            <Avatar key={item?.patientId?.id}>
                              {item?.patientId?.name?.charAt(0).toUpperCase()}
                            </Avatar>
                            </Tooltip>
                          ))}
                        </Avatar.Group>
                      );
                    })()}
                  </Col>
                  <Col xs={24} sm={12}>
                    {" "}
                    <InfoLabel>Physician</InfoLabel>
                    {journey?.physicianId ? (
                      <InfoValue>{`Dr. ${journey?.physicianId?.name}`}</InfoValue>
                    ) : ("N/A")}
                  </Col>
                  <Col xs={24} sm={12}>
                    {" "}
                    <InfoLabel>Condition</InfoLabel>
                    <InfoValue>
                      {journey?.steps[0]?.video?.specialty || "N/A"}
                    </InfoValue>
                  </Col>
                  <Col xs={24} sm={12}>
                    {" "}
                    <InfoLabel>Status</InfoLabel>
                    <InfoValue
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <StatusTag
                        className={journey?.status?.toLowerCase()}
                      >
                        {journey?.status}
                        {/* {journey?.status || "active"} */}
                      </StatusTag>
                      <Dropdown overlay={statusMenu(journey)} trigger={["click"]} getPopupContainer={triggerNode => triggerNode.parentNode}>
                        <EditOutlined
                          style={{
                            cursor: "pointer",
                            color: "var(--secondary-text-color)",
                            marginLeft: "8px",
                          }}
                        />
                      </Dropdown>
                    </InfoValue>
                  </Col>
                </Row>

                <ProgressContainer>
                  <ProgressText>
                    <span>Progress</span>
                    <span className="font-semibold">
                      Completed ({progress || 0}%)
                    </span>
                  </ProgressText>
                  <StyledProgress
                    percent={progress}
                    showInfo={false}
                    strokeColor="#8a3ef4"
                  />
                </ProgressContainer>

                <DateInfo>
                  <span>
                    <CalendarOutlined />
                  </span>
                  <span>
                    Created{" "}
                    {journey?.createdAt
                      ? new Date(journey.createdAt).toLocaleDateString()
                      : ""}
                  </span>
                  <span>•</span>
                  <span>
                    Last accessed{" "}
                    {journey?.updatedAt
                      ? new Date(journey.updatedAt).toLocaleDateString()
                      : ""}
                  </span>
                </DateInfo>

                <CardActions>
                  <PreviewButton onClick={() => handlePreviewClick(journey)}>
                    <EyeOutlined />
                    Preview
                  </PreviewButton>
                  <PreviewButton
                    onClick={() => handleAssignButton(journey)}
                  >
                    <FiUserPlus />
                    Assign Journey
                  </PreviewButton>
                </CardActions>
              </JourneyCardWrapper>
            </Col>
          );
        })}
      </Row>
      {hasMore && (
        <Row className="mt-25 w-100 d-flex justify-content-center" style={{ padding: '20px 0' }}>
          <CustomSecondaryButton onClick={handleLoadMore} loading={loadMoreLoading}>
            Load More Journeys
          </CustomSecondaryButton>
        </Row>
      )}
      {!hasMore && journeys.length > 0 && (
        <Row className="mt-25 w-100 d-flex justify-content-center" style={{ padding: '20px 0' }}>
          <p style={{ color: '#666', fontStyle: 'italic' }}>No more journeys to load</p>
        </Row>
      )}
    </>
  );
};

export default ClinicalJourneyCard;
